import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight } from 'lucide-react';
import { PublicHeader } from '@/components/PublicHeader';
import { PublicFooter } from '@/components/PublicFooter';
import { PUBLIC_CONTAINER } from '@/components/PublicContainer';

const API = process.env.REACT_APP_BACKEND_URL;

export function formatDate(ds) {
  if (!ds) return '';
  const d = new Date(`${ds}T00:00:00`);
  if (isNaN(d)) return ds;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function Card({ item, featured }) {
  const hasImg = !!(item.image_url && item.image_url.trim());
  return (
    <Link
      to={`/news-research/${item.slug}`}
      data-testid={`news-card-${item.slug}`}
      className={`group block rounded-2xl border transition-all hover:shadow-md overflow-hidden ${featured ? 'border-brand/30 bg-[#fafaf7]' : 'border-slate-200 bg-white'}`}
    >
      {hasImg && (
        <div className={`w-full overflow-hidden bg-slate-100 ${featured ? 'aspect-[21/9]' : 'aspect-[16/9]'}`}>
          <img src={item.image_url} alt={item.image_alt || item.title}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" loading="lazy" />
        </div>
      )}
      <div className={featured ? 'p-7 md:p-8' : 'p-6'}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-bold tracking-wider uppercase text-brand">{item.category}</span>
          {featured && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-brand/10 text-brand">Featured</span>}
          {item.published_date && <span className="text-xs text-slate-400">· {formatDate(item.published_date)}</span>}
        </div>
        <h2 className={`mt-2 font-bold text-slate-900 leading-snug ${featured ? 'text-2xl md:text-3xl' : 'text-lg'}`}>{item.title}</h2>
        {item.summary && <p className="mt-3 text-[15px] text-slate-600 leading-relaxed line-clamp-3">{item.summary}</p>}
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
          Read More <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );
}

export default function NewsResearchList() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'News & Research · Sergeant TAP';
    axios.get(`${API}/api/public/news-research`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, []);

  const featured = (items || []).find((i) => i.featured);
  const rest = (items || []).filter((i) => !featured || i.slug !== featured.slug);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="news-research-page">
      <PublicHeader />
      <main className="flex-1">
        <section className={`${PUBLIC_CONTAINER} pt-12 pb-14`}>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900" data-testid="news-heading">News &amp; Research</h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl" data-testid="news-subhead">
            Research, updates, and resources on military transition, workforce development, and military talent.
          </p>

          {items === null ? (
            <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand" /></div>
          ) : items.length === 0 ? (
            <div className="mt-12 rounded-2xl border border-slate-200 p-12 text-center" data-testid="news-empty">
              <p className="text-slate-700 font-medium">No published items yet</p>
              <p className="text-sm text-slate-400 mt-1">Check back soon for research and updates.</p>
            </div>
          ) : (
            <div className="mt-10 space-y-8" data-testid="news-list">
              {featured && <Card item={featured} featured />}
              {rest.length > 0 && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {rest.map((i) => <Card key={i.slug} item={i} />)}
                </div>
              )}
            </div>
          )}
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}
